import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, User, Mail, Phone, Building2, Loader2 } from 'lucide-react';
import { PageHeader } from '../components/common/PageHeader';
import { DataTable, Column } from '../components/common/DataTable';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { RestrictionBadge } from '../components/common/RestrictionBadge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Employee, RestrictionLevel } from '../types';
import { getEmployeesPaginated, getCompaniesPaginated } from '../api-clients';
import { EmployeeDto, CompanyDto } from '../web-api-client';
import { PaginationControls } from '../components/common/PaginationControls';
import { useToast } from '../hooks/use-toast';
import { getErrorToast } from '../lib/error-utils';

// Helper function to map EmployeeDto to Employee
function mapEmployeeDtoToEmployee(employee: EmployeeDto): Employee {
  return {
    id: employee.id?.toString() || '',
    name: employee.fullName || '',
    email: employee.email || '',
    phone: employee.phoneNumber || '',
    role: employee.role,
    companyId: employee.companyId?.toString() || '',
    companyName: employee.companyName || 'N/A',
    status: employee.isActive ? 'active' : 'inactive',
    createdAt: employee.createdAt ? new Date(employee.createdAt).toISOString() : new Date().toISOString(),
    walletConfigured: !!employee.walletAddress,
    restrictionLevel: (employee.restrictionLevel ?? 0) as RestrictionLevel,
  };
}

export default function Employees() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [companies, setCompanies] = useState<CompanyDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [companyFilter, setCompanyFilter] = useState<string>('all');

  // Load companies for the filter dropdown
  useEffect(() => {
    loadCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchTerm, companyFilter]);

  const loadCompanies = async () => {
    try {
      const data = await getCompaniesPaginated({ page: 1, pageSize: 1000 });
      setCompanies(data.items || []);
    } catch (err) {
      console.error('Failed to load companies:', err);
    }
  };

  const loadEmployees = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await getEmployeesPaginated({
        page: currentPage,
        pageSize: pageSize,
        searchTerm: searchTerm || undefined,
        companyId: companyFilter !== 'all' ? parseInt(companyFilter) : undefined,
      });
      
      // Map employees
      const mappedEmployees = (data.items || []).map(emp => mapEmployeeDtoToEmployee(emp));
      
      setEmployees(mappedEmployees);
      setTotalPages(data.metadata?.totalPages || 1);
      setTotalCount(data.metadata?.totalCount || 0);
    } catch (err) {
      const errorToast = getErrorToast(err, 'Erro ao carregar colaboradores', 'Failed to load employees');
      setError(errorToast.description);
      toast({
        title: errorToast.title,
        description: errorToast.description,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleCompanyFilter = (value: string) => {
    setCompanyFilter(value);
    setCurrentPage(1);
  };

  const columns: Column<Employee>[] = [
    {
      key: 'name',
      header: 'Colaborador',
      render: (employee) => (
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
            <User className="h-5 w-5 text-primary" />
          </div>
          <div>
            <p className="font-medium">{employee.name}</p>
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <Mail className="h-3 w-3" />
              <span>{employee.email}</span>
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'phone',
      header: 'Telefone',
      render: (employee) => (
        <div className="flex items-center gap-2 text-sm">
          <Phone className="h-4 w-4 text-muted-foreground" />
          <span>{employee.phone || 'N/A'}</span>
        </div>
      ),
    },
    {
      key: 'company',
      header: 'Empresa',
      render: (employee) => (
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-muted-foreground" />
          <span>{employee.companyName}</span>
        </div>
      ),
    },
    {
      key: 'role',
      header: 'Função',
      render: (employee) => (
        <Badge variant="secondary" className="capitalize">
              {employee.role === 'CompanyDriver' && 'Motorista'}
              {employee.role === 'CompanyManager' && 'Gestor'}
              {employee.role === 'CompanyDispatcher' && 'Expedidor'}
              {employee.role === 'CompanyAdministrator' && 'Administrador'}
              {employee.role === 'CompanyEmployee' && 'Colaborador'}
        </Badge>
      ),
    },
    {
      key: 'restrictionLevel',
      header: 'Nível de Restrição',
      render: (employee) => (
        <RestrictionBadge level={employee.restrictionLevel ?? 0} />
      ),
    },
    {
      key: 'status',
      header: 'Estado',
      render: (employee) => (
        <Badge variant={employee.status === 'active' ? 'default' : 'secondary'}>
          {employee.status === 'active' ? 'Ativo' : 'Inativo'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (employee) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/employees/${employee.id}`);
          }}
        >
          Ver Detalhes
        </Button>
      ),
      className: 'text-right',
    },
  ];

  if (loading && employees.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Colaboradores"
        description="Gerir colaboradores das empresas"
        actions={
          <Button onClick={() => navigate('/employees/new')}>
            <Plus className="h-4 w-4" />
            Novo Colaborador
          </Button>
        }
      />

      {error && (
        <div className="bg-destructive/10 text-destructive px-4 py-3 rounded-md">
          {error}
        </div>
      )}

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <Input
          placeholder="Pesquisar colaboradores..."
          value={searchTerm}
          onChange={(e) => handleSearch(e.target.value)}
          className="max-w-sm"
        />
        <Select value={companyFilter} onValueChange={handleCompanyFilter}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Filtrar por empresa" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as empresas</SelectItem>
            {companies.map((company) => (
              <SelectItem key={company.id} value={company.id?.toString() || ''}>
                {company.companyName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <DataTable
        columns={columns}
        data={employees}
        onRowClick={(employee) => navigate(`/employees/${employee.id}`)}
        emptyMessage="Nenhum colaborador encontrado"
      />

      <PaginationControls
        currentPage={currentPage}
        totalPages={totalPages}
        totalCount={totalCount}
        itemsShown={employees.length}
        itemLabel="colaboradores"
        onPageChange={setCurrentPage}
      />
    </div>
  );
}
